import React, { ReactElement, useEffect, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Tooltip as ReactTooltip } from 'react-tooltip';
import { Spinner } from 'react-bootstrap';
import { NavLink } from 'react-router-dom';
import { skipToken } from '@reduxjs/toolkit/dist/query';
import { LegislativeSessionLegacyEndpoint } from '../../../../interface/src/types/LegislativeSession';
import { EndOfSessionReport } from '@enview/interface/types/bills/EndOfSessionReport';
import {
  InsightsPolicyTopicType,
  InsightsPolicyTopic,
} from '@enview/interface/types/bills/InsightsPolicyTopic';
import {
  RichTextNode,
  RichTextNodeType,
  UserAction,
} from '@enview/interface/types/actions/UserAction';
import { FeedbackType } from '@enview/interface/types/actions/FeedbackData';
import * as BillAnalytics from '../../analytics/BillAnalytics';
import FeedbackPrompt from '../Shared/Prompts/FeedbackPrompt';
import ChevronIcon from '../../components/svg/chevron_big_up';
import Sparkles from '../../components/svg/Sparkles';
import Info from '../../components/svg/InfoCircleIcon';
import '../Shared/Search/Search.scss';
import Hideable from '../../components/Hideable';
import CommentForm from '../Shared/ActivityFeed/CommentForm/CommentForm';
import { BillAPI } from '../../api';
import { getTeamMode, setTeamMode } from '../../dux';

type EndOfSessionReportProps = {
  allTopics: InsightsPolicyTopic[] | undefined;
  jurisdictionAbbreviation: string;
  sessionsSelected: string[];
  allSessions: LegislativeSessionLegacyEndpoint[];
};

const EndOfSessionReportHideable = (props: EndOfSessionReportProps): ReactElement => {
  // props
  const { allTopics, jurisdictionAbbreviation, sessionsSelected, allSessions } = props;

  // Remote state
  const currentWorkspaceId = useSelector(getTeamMode);
  const dispatch = useDispatch();

  // Set the jurisdiction and session based on jurisdiction/session selected by user + available sessions
  const sessionSelectedName = sessionsSelected[0];
  const sessionSelected = allSessions.find(
    (session) =>
      session.displayName === sessionSelectedName ||
      (sessionSelectedName === 'Current Sessions' && session.isCurrent === true),
  );

  const jurisdictionSessionsWithReports = allSessions.filter(
    (session) =>
      session.reportTopicIds.length > 0 &&
      session.jurisdictionAbbreviation === jurisdictionAbbreviation,
  );
  if (jurisdictionSessionsWithReports.length === 0) {
    // we can't actually display anything, because none of the legislative sessions in this juris have reports
    return <></>;
  }

  // local state
  const [policyTopic, setPolicyTopic] = useState<string | null>(null);
  // eslint-disable-next-line @typescript-eslint/no-redundant-type-constituents
  const [savedComment, setSavedComment] = useState<Partial<UserAction> | null>(null);
  const eosrContainerRef = useRef<HTMLDivElement>(null);
  const [isSummaryAccurate, setIsSummaryAccurate] = useState<boolean | undefined>(
    undefined,
  );

  // callback provided to CommentForm to change content after comment is saved
  const onCommentSaveCallback = (userAction: Partial<UserAction>): void => {
    setSavedComment(userAction);
    // Often an EOSR report is very long, so we will be quite far down the page in bill results
    // unless we scroll back up after submitting
    setTimeout(() => {
      if (eosrContainerRef && eosrContainerRef.current) {
        eosrContainerRef.current.scrollIntoView({
          behavior: 'smooth',
        });
      }
    }, 400);
  };

  /* only request the EOSR API if feature flag is enabled and all required params
    are available */
  const eosrCriteria =
    policyTopic && jurisdictionAbbreviation && sessionSelected
      ? {
          jurisdictionAbbreviation: jurisdictionAbbreviation,
          sessionName: sessionSelected.name,
          policyTopicInsightName: policyTopic,
        }
      : null;
  const { data: report } = BillAPI.endpoints.getEndOfSessionReport.useQuery(
    eosrCriteria
      ? {
          criteria: eosrCriteria,
        }
      : skipToken,
  );

  // Handle accuracy feedback
  useEffect(() => {
    if (isSummaryAccurate !== undefined && policyTopic && report?.passedBillsSummary) {
      BillAnalytics.trackEndOfSessionReportFeedback(
        jurisdictionAbbreviation,
        policyTopic,
        isSummaryAccurate,
        report.passedBillsSummary,
      );
    }
  }, [isSummaryAccurate, policyTopic, report?.passedBillsSummary]);

  const title = (
    <h2 className="hideable-content">
      <div className="ml-1 title">
        <span>
          <span className="sparkle">
            <Sparkles />
          </span>
          <span className="m-2">Session Activity Report</span>
          <span data-tooltip-id={`end-of-session-report-detail`}>
            <Info />
          </span>
        </span>
        <ReactTooltip
          children={
            'Plural AI can summarize the results of the legislative session - click to expand and generate a summary'
          }
          className="tooltip-content"
          clickable
          delayHide={200}
          id={`end-of-session-report-detail`}
          place="bottom"
          style={{ maxWidth: '300px' }}
        />
        <span className="float-right arrow-icon">
          <ChevronIcon />
        </span>
      </div>
    </h2>
  );

  const placeholderRichTextNodes: RichTextNode = {
    children: [
      {
        type: RichTextNodeType.PARAGRAPH,
        children: [
          {
            text: 'Loading...',
          },
        ],
      },
    ],
  };
  const userActionDraft: Partial<UserAction> = {
    commentJson: report
      ? JSON.parse(report.passedBillsSummary)
      : placeholderRichTextNodes,
  };

  const formattedReport = (eosrReport: EndOfSessionReport): ReactElement => {
    BillAnalytics.trackEndOfSessionReportViewed(
      jurisdictionAbbreviation.toUpperCase(),
      (policyTopic && policyTopic.replace(/_/g, ' ')) ?? '',
      sessionSelected?.displayName ?? '',
    );
    return (
      <div className="formatted-report">
        <div className="context">
          On {policyTopic && policyTopic.replace(/_/g, ' ')} in{' '}
          {jurisdictionAbbreviation.toUpperCase()} {sessionSelected?.displayName}
        </div>
        <div className="context">
          Number of Bills Introduced: {eosrReport.numberOfBillsIntroduced}
        </div>
        <div className="context">
          Number of Bills Passed: {eosrReport.numberOfBillsPassed}
        </div>
        <div>
          The generated summary is below. As with any AI-generated content, please
          consider it a starting draft. You can review and edit the comment below. Click
          the Post button to post your edited version to the Activity Feed of your
          current workspace.
          <FeedbackPrompt
            feedbackType={FeedbackType.EndOfSessionReport}
            setFeedbackState={setIsSummaryAccurate}
            submitFeedbackMetadata={{
              jurisdictionAbbreviation: jurisdictionAbbreviation,
              insightsPolicyTopicName: policyTopic,
              summary: eosrReport.passedBillsSummary,
            }}
          />
        </div>
        <div className="comment-form-body">
          <CommentForm
            aiSummary={false}
            onSaveCallback={onCommentSaveCallback}
            userActionDraft={userActionDraft}
          />
        </div>
      </div>
    );
  };

  const spinner = (
    <Spinner
      animation="border"
      className="text-field-spinner"
      role="status"
      variant="secondary"
    />
  );

  const topicClickHandler =
    (policyTopicInsightsName: string) =>
    (event: React.MouseEvent<HTMLAnchorElement>) => {
      event.preventDefault();
      setPolicyTopic(policyTopicInsightsName);
    };

  const makePolicyTopicPicker = (
    allTopicOptions: InsightsPolicyTopic[],
    selectedSession: LegislativeSessionLegacyEndpoint | undefined,
  ): ReactElement => (
    <div>
      <p className="eosr-explainer-text">
        Plural Policy has developed a session activity report generator that supports
        this jurisdiction. Select a policy topic to see what related action the
        legislature has taken in the given session. Ongoing sessions will be summarized
        based on activity so far.
      </p>
      <ul>
        {allTopicOptions
          .filter(
            // filter out the "untaggable" policy topic, since there is no report for that
            (topicOption) =>
              topicOption.policyTopic != InsightsPolicyTopicType.UNTAGGABLE,
          )
          .filter(
            // filter out any topic that is not available for this session
            (topicOption) => selectedSession?.reportTopicIds.includes(topicOption.id),
          )
          .map((topicOption) => (
            <li key={topicOption.id}>
              <a href="#" onClick={topicClickHandler(topicOption.insightName)}>
                {topicOption.policyTopic}
              </a>
            </li>
          ))}
      </ul>
      <p className="eosr-explainer-text">
        Not seeing the reports you're looking for? Ensure you have the correct session
        selected from the sessions dropdown.
      </p>
    </div>
  );

  const makePostCommentNotice = (commentPosted: Partial<UserAction>): ReactElement => {
    if (!commentPosted || !commentPosted.teams) {
      return <div>error</div>;
    }

    let navigation = null;
    if (commentPosted.teams.length === 1) {
      const commentWorkspaceId = commentPosted.teams[0];
      const changeWorkspace = (): void => {
        if (currentWorkspaceId === commentWorkspaceId) {
          return;
        }
        dispatch(setTeamMode(commentWorkspaceId));
      };
      navigation = (
        <p>
          <NavLink
            activeClassName="active-nav-link"
            onClick={changeWorkspace}
            to="/teams/activity"
          >
            <strong>View Workspace</strong>
          </NavLink>
        </p>
      );
    }

    return (
      <div>
        <p>
          Your report has been posted. You can view it now on the Team Activity feed if
          you want, or hide this element to continue with your search.
        </p>

        {navigation}
      </div>
    );
  };

  if (sessionsSelected.length > 1 || !sessionSelected?.reportTopicIds.length) {
    // Hide EOSR dropdown when a user has multiple sessions selected
    return <></>;
  }

  // Choose content to display
  let content = spinner;
  if (savedComment) {
    // user just saved a comment, give user a chance to navigate to that team to see it
    content = makePostCommentNotice(savedComment);
  } else if (report) {
    // report has been fetched, display it
    content = formattedReport(report);
  } else if (allTopics && allTopics.length > 0) {
    // show the UI to select a topic and generate report for that topic
    content = makePolicyTopicPicker(allTopics, sessionSelected);
  }

  return (
    <div className="end-of-session-report-container" ref={eosrContainerRef}>
      <Hideable title={title}>{content}</Hideable>
    </div>
  );
};

export default EndOfSessionReportHideable;
